import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { Truck } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, Input, Select, Skeleton, StatusBadge } from '../../components/ui'

export default function TruckDetail() {
  const { truckId } = useParams<{ truckId: string }>()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'truck', truckId],
    queryFn: () => api.get<{ ok: boolean; item: Truck }>(`/leader/trucks/${truckId}`),
    enabled: Boolean(truckId),
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/leader/trucks/${truckId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'truck', truckId] })
      void qc.invalidateQueries({ queryKey: ['leader', 'trucks'] })
      setEditing(false)
    },
  })

  if (!truckId) return <Navigate to="/trucks" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/trucks">Back to fleet</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const truck = data.item

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    save.mutate({
      name: String(form.get('name') ?? '').trim(),
      driver_name: String(form.get('driver_name') ?? '').trim(),
      driver_phone: String(form.get('driver_phone') ?? '').trim(),
      status: String(form.get('status') ?? truck.status ?? 'active'),
    })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{truck.name}</h1>
          <p className="page-head__sub">{truck.plate}</p>
        </div>
        <div className="row">
          <StatusBadge status={truck.status || 'active'} />
          {!editing && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Edit</Button>}
        </div>
      </div>

      {editing ? (
        <Card>
          <CardHeader title="Edit truck" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {save.isError && (
                  <Alert tone="danger">
                    {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
                  </Alert>
                )}
                <Input label="Name" name="name" defaultValue={truck.name} />
                <div className="grid grid--2">
                  <Input label="Driver name" name="driver_name" defaultValue={truck.driver_name} />
                  <Input label="Driver phone" name="driver_phone" defaultValue={truck.driver_phone} />
                </div>
                <Select label="Status" name="status" defaultValue={truck.status || 'active'}>
                  <option value="active">Active</option>
                  <option value="maintenance">In maintenance</option>
                  <option value="retired">Retired</option>
                </Select>
                <div className="row">
                  <Button type="submit" loading={save.isPending} confirm={{ title: "Save these changes?" }}>Save</Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid--2">
          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <div className="stack">
                <Row label="Ownership" value={titleCase(truck.ownership) || '—'} />
                <Row label="Capacity" value={truck.capacity_tonnes ? `${truck.capacity_tonnes} t` : '—'} />
                <Row label="Make / model" value={[truck.make, truck.model].filter(Boolean).join(' ') || '—'} />
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Driver" />
            <CardBody>
              <div className="stack">
                <Row label="Name" value={truck.driver_name || '—'} />
                <Row label="Phone" value={truck.driver_phone || '—'} />
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
